import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Condition } from "@types-runway/condition";
import { RemovedMedication, RemovedMedicationsState } from "@types-runway/removedMedication";

import { clearNewCase } from "../cases/casesSlice";

const initialState: RemovedMedicationsState = {};

interface MedicationRemovedPayload {
  caseId: number;
  condition: Condition;
  productOrder: string[];
}

interface MedicationRestoredPayload {
  caseId: number;
  productName: string;
}

const productNameOf = (removed: RemovedMedication) => removed.condition.product.name;

export const removedMedicationsSlice = createSlice({
  name: "removedMedications",
  initialState,
  reducers: {
    medicationRemoved: (state, action: PayloadAction<MedicationRemovedPayload>) => {
      const { caseId, condition, productOrder } = action.payload;
      const keptForCase = (state[caseId] ?? []).filter(
        (removed) => productNameOf(removed) !== condition.product.name
      );

      return { ...state, [caseId]: [...keptForCase, { condition, productOrder }] };
    },
    medicationRestored: (state, action: PayloadAction<MedicationRestoredPayload>) => {
      const { caseId, productName } = action.payload;
      const remaining = (state[caseId] ?? []).filter(
        (removed) => productNameOf(removed) !== productName
      );

      return { ...state, [caseId]: remaining };
    },
    removedMedicationsCleared: (state, action: PayloadAction<{ caseId: number }>) => {
      const { [action.payload.caseId]: ignored, ...otherCases } = state;

      return otherCases;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearNewCase, () => initialState);
  },
});

export const { medicationRemoved, medicationRestored, removedMedicationsCleared } =
  removedMedicationsSlice.actions;

export const selectRemovedMedications = (
  state: { removedMedications: RemovedMedicationsState },
  caseId: number | undefined
): RemovedMedication[] => (caseId ? (state.removedMedications[caseId] ?? []) : []);

export default removedMedicationsSlice.reducer;
